import React, { useEffect, useState } from 'react';
import { Table, Spin, Empty } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { NumericFormat } from 'react-number-format';
import { useSelector } from 'react-redux';
import styles from './index.module.scss';

interface SummaryGroup {
    accountName: string;
    customerName: string;
    className: string;
    locationName: string;
    totalAmount: number;
    // For React key generation
    key?: string;
}

interface SummaryTableProps {
    data: SummaryGroup[];
    grandTotal: number;
    loading: boolean;
    onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
    currentSort?: { sortBy: string; sortOrder: 'asc' | 'desc' };
    hasMore?: boolean;
    isLoadingMore?: boolean;
    onLoadMore?: () => void;
    selectedAccounts?: string[];
    selectedCustomers?: string[];
    selectedClasses?: string[];
    selectedLocations?: string[];
}

const SummaryTable: React.FC<SummaryTableProps> = ({
    data,
    grandTotal,
    loading,
    onSort,
    currentSort,
    hasMore,
    isLoadingMore,
    onLoadMore,
    selectedAccounts = [],
    selectedCustomers = [],
    selectedClasses = [],
    selectedLocations = [],
}) => {
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [showNoMoreMessage, setShowNoMoreMessage] = useState(false);
    const { configurations: configurationsData } = useSelector(
        (state: any) => state.companies
    );

    const hasActiveFilters = Boolean(
        selectedAccounts.length > 0 ||
        selectedCustomers.length > 0 ||
        selectedClasses.length > 0 ||
        selectedLocations.length > 0
    );
    // Server-side sorting
    const handleSort = (key: string) => {
        if (onSort) {
            const newSortOrder = currentSort?.sortBy === key && currentSort?.sortOrder === 'asc' ? 'desc' : 'asc';
            onSort(key, newSortOrder);
        }
    };

    // Reset scroll tracking when hasMore changes
    useEffect(() => {
        if (hasMore) {
            setHasScrolledToBottom(false);
            setShowNoMoreMessage(false);
        }
    }, [hasMore]);

    // Auto-hide "No more groups" message after 3 seconds
    useEffect(() => {
        if (!hasMore && !isLoadingMore && hasScrolledToBottom && data.length > 0) {
            setShowNoMoreMessage(true);
            const timer = setTimeout(() => {
                setShowNoMoreMessage(false);
            }, 3000);

            return () => {
                clearTimeout(timer);
            };
        } else {
            setShowNoMoreMessage(false);
        }
    }, [hasMore, isLoadingMore, hasScrolledToBottom, data.length]);

    // Infinite scroll logic
    useEffect(() => {
        const handleScroll = (event: any) => {
            const { scrollTop, scrollHeight, clientHeight } = event.target;
            const threshold = 10; // Load more when 10px from bottom

            if (scrollTop + clientHeight >= scrollHeight - threshold) {
                if (!hasMore) {
                    // User scrolled to bottom but no more data
                    setHasScrolledToBottom(true);
                    return;
                }
                if (!isLoadingMore && !loading) {
                    onLoadMore?.();
                }
            }
        };

        // Attach scroll listener to the actual table body element
        const tableBody = document.querySelector('.ant-table-body');
        if (tableBody) {
            tableBody.addEventListener('scroll', handleScroll);
            return () => {
                tableBody.removeEventListener('scroll', handleScroll);
            };
        }
    }, [hasMore, isLoadingMore, loading, onLoadMore]);

    const getSortIcon = (key: string) => {
        if (currentSort?.sortBy !== key) {
            return <ArrowUpOutlined className={styles['sort-icon-inactive']} />;
        }
        return currentSort.sortOrder === 'asc'
            ? <ArrowUpOutlined className={styles['sort-icon-active']} />
            : <ArrowDownOutlined className={styles['sort-icon-active']} />;
    };

    const CustomEmptyState = () => (
        <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
                loading
                    ? null
                    : hasActiveFilters
                        ? "No Transaction Summary records matches the selected filter criteria"
                        : "No Transaction Summary records are currently available"
            }
            style={{
                padding: '40px 20px',
                margin: '20px 0'
            }}
        />
    );

    // Fixed column configuration - always shows all 4 fields
    const columns = [
        {
            title: (
                <div
                    className={styles['sortable-header']}
                    onClick={() => handleSort('accountName')}
                >
                    Account
                    {getSortIcon('accountName')}
                </div>
            ),
            dataIndex: 'accountName',
            key: 'accountName',
            ellipsis: true,
            width: 200,
            render: (text: string) => text || '',
        },
        {
            title: (
                <div
                    className={styles['sortable-header']}
                    onClick={() => handleSort('customerName')}
                >
                    Customer
                    {getSortIcon('customerName')}
                </div>
            ),
            dataIndex: 'customerName',
            key: 'customerName',
            ellipsis: true,
            width: 200,
            render: (text: string) => text || '',
        },
        {
            title: (
                <div
                    className={styles['sortable-header']}
                    onClick={() => handleSort('className')}
                >
                    Class
                    {getSortIcon('className')}
                </div>
            ),
            dataIndex: 'className',
            key: 'className',
            ellipsis: true,
            width: 170,
            render: (text: string) => text || '',
        },
        {
            title: (
                <div
                    className={styles['sortable-header']}
                    onClick={() => handleSort('locationName')}
                >
                    Location
                    {getSortIcon('locationName')}
                </div>
            ),
            dataIndex: 'locationName',
            key: 'locationName',
            ellipsis: true,
            width: 170,
            render: (text: string) => text || '',
        },
        {
            title: (
                <div
                    className={styles['sortable-header']}
                    onClick={() => handleSort('vendorName')}
                >
                    Vendor
                    {getSortIcon('vendorName')}
                </div>
            ),
            dataIndex: 'vendorName',
            key: 'vendorName',
            ellipsis: true,
            width: 170,
            render: (text: string) => text || '',
        },
        {
            title: (
                <div
                    className={styles['sortable-header']}
                    onClick={() => handleSort('totalAmount')}
                    style={{ display: 'flex', justifyContent: 'flex-end', cursor: 'pointer' }}
                >
                    Amount
                    {getSortIcon('totalAmount')}
                </div>
            ),
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            width: 110,
            align: 'right' as const,
            render: (amount: number) => (
                <NumericFormat
                    value={amount}
                    thousandSeparator=","
                    decimalScale={configurationsData?.decimalToFixedAmount || 2}
                    fixedDecimalScale={true}
                    displayType="text"
                    prefix="$"
                    renderText={(value) => (
                        <span className={styles['amount-cell']}>
                            {value ?? 0}
                        </span>
                    )}
                />
            ),
        },
    ];

    return (
        <div className={styles['summary-table-container']}>
            <div className={styles['summary-header']}>
                <h3 className={styles['summary-title']}>
                    Summary by Account, Customer, Class, Location, Vendor
                </h3>
            </div>
            <div className={styles['table-wrapper']}>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey={(record) => record.key || `${record.accountName}-${record.customerName}-${record.className}-${record.locationName}`}
                    pagination={false}
                    scroll={{ x: 1000, y: 'calc(87vh - 420px)' }}
                    loading={loading}
                    className={`${styles['summary-table']} summary-table`}
                    showHeader={true}
                    locale={{
                        emptyText: <CustomEmptyState />
                    }}
                />
                {/* Sticky Total Row */}
                <div className={styles['sticky-total-row']}>
                    <div className={styles['total-row-content']} style={{ display: 'flex' }}>
                        <div className={styles['total-cell']} style={{ flex: 4 }}>Grand Total</div>
                        <div className={styles['total-cell']} style={{ flex: 1, textAlign: 'right' }}>
                            <NumericFormat
                                value={grandTotal}
                                thousandSeparator=","
                                decimalScale={configurationsData?.decimalToFixedAmount || 2}
                                fixedDecimalScale={true}
                                displayType="text"
                                prefix="$"
                                renderText={(value) => (
                                    <span className={styles['amount-cell']}>
                                        {value ?? 0}
                                    </span>
                                )}
                            />
                        </div>
                    </div>
                </div>
                {/* Loading and status messages positioned above sticky total */}
                <div className={styles['status-messages']}>
                    {isLoadingMore && (
                        <div style={{
                            textAlign: 'right',
                            padding: '0 20px',
                            fontSize: '12px',
                            color: '#344735',
                            marginTop: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end'
                        }}>
                            <Spin size="small" className={styles['loading-more-spinner']} />
                            <span style={{ marginLeft: '8px' }}>Loading more groups...</span>
                        </div>
                    )}
                    {showNoMoreMessage && (
                        <div style={{
                            textAlign: 'right',
                            padding: '0 20px',
                            color: '#344735',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            marginTop: '8px'
                        }}>
                            No more groups
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SummaryTable;
